import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { ExpirationMonthInput } from "../components/ExpirationMonthInput";
import { ModalCloseButton } from "../components/ModalCloseButton";
import { MoveStockSheet, type MoveApparatusOption } from "../components/MoveStockSheet";
import { StatusBadge } from "../components/StatusBadge";
import { api } from "../lib/api";
import { formatExpirationMonth, normalizeExpirationDate } from "../lib/expiration";
import { buildRelatedRowMeta, relatedRowClass } from "../lib/relatedRows";
import { clearStockEditToken, getStockEditToken, setStockEditToken as persistStockEditToken } from "../lib/session";
import {
  filterStockItemsByStatus,
  STOCK_STATUS_FILTER_OPTIONS,
  type StockStatusFilter
} from "../lib/stockStatusFilter";
import type { CatalogSearchResult, StockLotItem } from "../lib/types";
import { stockRowsToCsv } from "../lib/stockExport";
import {
  applyConfirmedStockRows,
  aggregateStockSaveResults,
  isConfirmedStockSaveChunk,
  isUnconfirmedStockMutationError,
  partialStockSaveMessage,
  removeConfirmedStockDrafts
} from "../lib/stockSave";
import { useRegisterViewRefresh, useUnsavedChangesWarning } from "../lib/viewRefresh";

interface AddStockOption extends CatalogSearchResult {
  allowedInStock: boolean;
}

interface StockDraft {
  binLabel: string;
  quantity: string;
  parQty: string;
  expirationDate: string;
  notes: string;
}

interface AddStockForm {
  query: string;
  selectedItem: AddStockOption | null;
  quantity: string;
  expirationDate: string;
  binLabel: string;
  notes: string;
}

function useDebounced(value: string, ms = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

function normalizeQuantityInput(raw: string): string {
  const digits = raw.replace(/[^\d]/g, "");
  if (!digits) return "";
  return String(Number(digits));
}

function parseQuantityInput(raw: string): number {
  const normalized = normalizeQuantityInput(raw);
  if (!normalized) return 0;
  return Number(normalized);
}

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      out.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  out.push(current);
  return out;
}

function parseCsv(text: string): string[][] {
  const normalized = text.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = normalized.split("\n").filter((line) => line.trim().length > 0);
  return lines.map((line) => parseCsvLine(line));
}

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, "").replace(/_/g, "");
}

function initialAddForm(): AddStockForm {
  return {
    query: "",
    selectedItem: null,
    quantity: "",
    expirationDate: "",
    binLabel: "",
    notes: ""
  };
}

function draftFromStockItem(item: StockLotItem): StockDraft {
  return {
    binLabel: item.binLabel ?? "",
    quantity: String(item.quantity),
    parQty: String(item.parQty),
    expirationDate: item.expirationDate ?? "",
    notes: item.notes ?? ""
  };
}

export function StockRoomScreen({ enteredName, rememberName }: { enteredName: string; rememberName: (name: string) => void }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StockStatusFilter>("all");
  const [editorName, setEditorName] = useState(enteredName);
  const [items, setItems] = useState<StockLotItem[]>([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [stockLoading, setStockLoading] = useState(true);
  const [stockReconciliationRequired, setStockReconciliationRequired] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<StockLotItem | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [stockEditToken, setStockEditToken] = useState(() => getStockEditToken());
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockPin, setUnlockPin] = useState("");
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [unlockError, setUnlockError] = useState("");

  const [drafts, setDrafts] = useState<Record<string, StockDraft>>({});

  const [showAdd, setShowAdd] = useState(false);
  const [addModalMode, setAddModalMode] = useState<"add" | "split">("add");
  const [addNewItemMode, setAddNewItemMode] = useState(false);
  const [addUseCustomBin, setAddUseCustomBin] = useState(false);
  const [addForm, setAddForm] = useState<AddStockForm>(initialAddForm());
  const [addResults, setAddResults] = useState<AddStockOption[]>([]);
  const [addBinSuggestions, setAddBinSuggestions] = useState<string[]>([]);
  const [addLoading, setAddLoading] = useState(false);
  const [addBusy, setAddBusy] = useState(false);

  const [moveSelected, setMoveSelected] = useState<StockLotItem | null>(null);
  const [moveOptions, setMoveOptions] = useState<MoveApparatusOption[]>([]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const stockRequestIdRef = useRef(0);
  const stockAbortRef = useRef<AbortController | null>(null);
  const pendingDraftsRef = useRef(false);
  const debouncedSearch = useDebounced(search, 300);
  const debouncedAddQuery = useDebounced(addForm.query, 220);
  const stockUnlocked = stockEditToken.length > 0;

  useEffect(() => {
    setEditorName(enteredName);
  }, [enteredName]);

  const cancelStockRead = () => {
    stockRequestIdRef.current += 1;
    stockAbortRef.current?.abort();
    stockAbortRef.current = null;
    setStockLoading(false);
  };

  const refresh = async (
    query = debouncedSearch,
    options: { allowWithDrafts?: boolean; forceRefresh?: boolean } = {}
  ) => {
    const requestId = ++stockRequestIdRef.current;
    stockAbortRef.current?.abort();
    const controller = new AbortController();
    stockAbortRef.current = controller;
    setStockLoading(true);
    setError("");
    try {
      const result = await api.getStockInventory(query, {
        signal: controller.signal,
        dedupe: false,
        forceRefresh: options.forceRefresh
      });
      if (
        requestId !== stockRequestIdRef.current ||
        (!options.allowWithDrafts && pendingDraftsRef.current)
      ) {
        return;
      }
      setItems(result.items);
    } catch (err) {
      if (requestId !== stockRequestIdRef.current) return;
      throw err;
    } finally {
      if (requestId === stockRequestIdRef.current) {
        setStockLoading(false);
      }
    }
  };

  const forceReconcileStock = async () => {
    await refresh(debouncedSearch, { allowWithDrafts: true, forceRefresh: true });
    setStockReconciliationRequired(false);
  };

  const reconcileStockView = async () => {
    setError("");
    setNotice("");
    try {
      await forceReconcileStock();
      setNotice("Stock reconciled with the server. Only still-highlighted changes remain pending.");
    } catch (err: any) {
      setStockReconciliationRequired(true);
      setError(
        `Stock could not be reconciled. Keep this screen open and try Reconcile Stock again when the connection improves. ${
          err?.message ?? ""
        }`.trim()
      );
    }
  };

  useEffect(() => {
    if (search !== debouncedSearch) {
      cancelStockRead();
      return;
    }
    if (pendingDraftsRef.current) return;
    refresh().catch((err: any) => setError(err?.message ?? "Failed to load stock detail"));
    return () => stockAbortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, debouncedSearch]);

  useEffect(() => {
    if (!showAdd) {
      setAddResults([]);
      setAddBinSuggestions([]);
      setAddUseCustomBin(false);
      setAddLoading(false);
      return;
    }

    if (addModalMode !== "add") {
      setAddResults([]);
      setAddLoading(false);
      return;
    }

    if (addForm.selectedItem || addForm.query !== debouncedAddQuery) {
      setAddResults([]);
      setAddLoading(false);
      return;
    }

    const q = debouncedAddQuery.trim();
    const currentQuery = addForm.query.trim();
    if (currentQuery !== q) {
      setAddResults([]);
      setAddLoading(currentQuery.length >= 2);
      return;
    }
    if (q.length < 2) {
      setAddResults([]);
      setAddLoading(false);
      return;
    }

    let active = true;
    const controller = new AbortController();
    setAddLoading(true);
    if (!addNewItemMode) {
      api
        .getAllowedStockItems(q, 40, {
          signal: controller.signal,
          dedupe: false
        })
        .then((result) => {
          if (!active || controller.signal.aborted) return;
          setAddResults(result.items.map((item) => ({ ...item, allowedInStock: true })));
        })
        .catch(() => {
          if (!active || controller.signal.aborted) return;
          setAddResults([]);
        })
        .finally(() => {
          if (!active || controller.signal.aborted) return;
          setAddLoading(false);
        });
      return () => {
        active = false;
        controller.abort();
      };
    }

    const requestOptions = {
      signal: controller.signal,
      dedupe: false
    };
    Promise.allSettled([
      api.getAllowedStockItems(q, 40, requestOptions),
      api.searchCatalog(q, 40, requestOptions)
    ])
      .then((responses) => {
        if (!active || controller.signal.aborted) return;

        const allowed =
          responses[0].status === "fulfilled"
            ? responses[0].value.items
            : [];
        const catalog =
          responses[1].status === "fulfilled"
            ? responses[1].value.items
            : [];

        const allowedIds = new Set(allowed.map((item) => item.id));
        const merged = new Map<string, AddStockOption>();

        for (const item of catalog) {
          merged.set(item.id, {
            ...item,
            allowedInStock: allowedIds.has(item.id)
          });
        }

        // Fallback for any allowed items not present in catalog search payload.
        for (const item of allowed) {
          if (merged.has(item.id)) continue;
          merged.set(item.id, {
            ...item,
            allowedInStock: true
          });
        }

        const options = [...merged.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
        setAddResults(options);
      })
      .finally(() => {
        if (!active || controller.signal.aborted) return;
        setAddLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    showAdd,
    addModalMode,
    debouncedAddQuery,
    addNewItemMode,
    addForm.query
  ]);

  useEffect(() => {
    if (!showAdd) {
      setAddBinSuggestions([]);
      setAddUseCustomBin(false);
      return;
    }
    const selectedId = addForm.selectedItem?.id;
    if (!selectedId) {
      setAddBinSuggestions([]);
      setAddUseCustomBin(addModalMode === "add" && addNewItemMode);
      return;
    }

    let active = true;
    const controller = new AbortController();
    setAddBinSuggestions([]);
    api
      .getStockLotsForCatalogItem(selectedId, {
        signal: controller.signal,
        dedupe: false
      })
      .then((result) => {
        if (!active || controller.signal.aborted) return;
        const bins = [...new Set(result.items.map((item) => (item.binLabel ?? "").trim()).filter((value) => value.length > 0))];
        setAddBinSuggestions(bins);
        const forceCustom = addNewItemMode;
        if (forceCustom || bins.length === 0) {
          setAddUseCustomBin(true);
          return;
        }

        setAddUseCustomBin(false);
        setAddForm((prev) => {
          if (!prev.selectedItem || prev.selectedItem.id !== selectedId) return prev;
          if (prev.binLabel.trim().length > 0 && bins.includes(prev.binLabel.trim())) return prev;
          return {
            ...prev,
            binLabel: bins[0]
          };
        });
      })
      .catch(() => {
        if (!active || controller.signal.aborted) return;
        setAddBinSuggestions([]);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [showAdd, addModalMode, addNewItemMode, addForm.selectedItem?.id]);

  const itemById = useMemo(() => {
    const map: Record<string, StockLotItem> = {};
    for (const item of items) map[item.stockLotId] = item;
    return map;
  }, [items]);

  const visibleItems = useMemo(
    () => filterStockItemsByStatus(items, statusFilter),
    [items, statusFilter]
  );

  const relatedMetaByStockLotId = useMemo(
    () =>
      buildRelatedRowMeta(
        visibleItems.map((item) => ({
          rowKey: item.stockLotId,
          catalogItemId: item.catalogItemId
        }))
      ),
    [visibleItems]
  );

  const hasDraft = (item: StockLotItem) => {
    const draft = drafts[item.stockLotId];
    if (!draft) return false;
    return (
      normalizeQuantityInput(draft.quantity || "0") !== String(item.quantity) ||
      normalizeQuantityInput(draft.parQty || "0") !== String(item.parQty) ||
      draft.expirationDate !== (item.expirationDate ?? "") ||
      draft.notes !== (item.notes ?? "") ||
      draft.binLabel !== (item.binLabel ?? "")
    );
  };

  const pendingCount = useMemo(() => items.filter((item) => hasDraft(item)).length, [items, drafts]);
  pendingDraftsRef.current = pendingCount > 0;
  const editorOpen = Boolean(showAdd || moveSelected || deleteConfirmItem || unlockOpen);
  const unsavedWork = pendingCount > 0 || editorOpen || stockReconciliationRequired;
  const stockBusy = saving || importing || deleteBusy || unlockBusy || addBusy || stockLoading;
  const stockTableEditingDisabled = !stockUnlocked || stockLoading || saving;
  useUnsavedChangesWarning(unsavedWork);
  useRegisterViewRefresh(
    async () => {
      if (stockReconciliationRequired) {
        await reconcileStockView();
        return;
      }
      setError("");
      await refresh();
    },
    {
      disabled: stockBusy || (unsavedWork && !stockReconciliationRequired),
      disabledReason:
        unsavedWork && !stockReconciliationRequired
          ? "Save or discard pending stock edits before refreshing."
          : "The stock workspace is busy."
    }
  );

  const getDraft = (item: StockLotItem): StockDraft => {
    return drafts[item.stockLotId] ?? draftFromStockItem(item);
  };

  const updateDraft = (stockLotId: string, patch: Partial<StockDraft>) => {
    if (!stockUnlocked) return;
    if (!pendingDraftsRef.current) cancelStockRead();
    setDrafts((prev) => {
      const base =
        prev[stockLotId] ??
        (itemById[stockLotId]
          ? draftFromStockItem(itemById[stockLotId])
          : {
              binLabel: "",
              quantity: "0",
              parQty: "0",
              expirationDate: "",
              notes: ""
            });
      return {
        ...prev,
        [stockLotId]: {
          ...base,
          ...patch
        }
      };
    });
  };

  const updateParDraft = (item: StockLotItem, rawValue: string) => {
    if (!stockUnlocked) return;
    if (!pendingDraftsRef.current) cancelStockRead();
    const parQty = normalizeQuantityInput(rawValue);
    setDrafts((prev) => {
      const next = { ...prev };
      let touched = false;
      for (const row of items) {
        if (row.catalogItemId !== item.catalogItemId) continue;
        const base = next[row.stockLotId] ?? draftFromStockItem(row);
        next[row.stockLotId] = {
          ...base,
          parQty
        };
        touched = true;
      }
      return touched ? next : prev;
    });
  };

  const buildPayloadRows = () => {
    const rows: Array<{
      stockLotId: string;
      updatedAt: string | null;
      parUpdatedAt: string | null;
      quantity: number;
      parQty: number;
      expirationDate: string | null;
      binLabel: string | null;
      notes: string;
    }> = [];

    for (const item of items) {
      const draft = drafts[item.stockLotId];
      if (!draft) continue;
      if (!hasDraft(item)) continue;

      const quantity = parseQuantityInput(draft.quantity || "0");
      if (!Number.isInteger(quantity) || quantity < 0) {
        throw new Error(`Quantity must be a whole number >= 0 for ${item.itemName}`);
      }
      const parQty = parseQuantityInput(draft.parQty || "0");
      if (!Number.isInteger(parQty) || parQty < 0) {
        throw new Error(`PAR must be a whole number >= 0 for ${item.itemName}`);
      }

      rows.push({
        stockLotId: item.stockLotId,
        updatedAt: item.updatedAt,
        parUpdatedAt: item.parUpdatedAt,
        quantity,
        parQty,
        expirationDate: quantity <= 0 ? null : draft.expirationDate ? draft.expirationDate : null,
        binLabel: draft.binLabel.trim() ? draft.binLabel.trim() : null,
        notes: draft.notes
      });
    }

    return rows;
  };

  const openUnlock = () => {
    setUnlockError("");
    setUnlockPin("");
    setUnlockOpen(true);
  };

  const lockStockEditing = () => {
    if (stockReconciliationRequired) {
      setError("Reconcile Stock before locking so an unconfirmed server change is not hidden.");
      return;
    }
    clearStockEditToken();
    setStockEditToken("");
    setUnlockOpen(false);
    setDrafts({});
    setNotice("Stock editing locked.");
  };

  const discardDrafts = () => {
    if (stockReconciliationRequired) {
      setError("Reconcile Stock before discarding changes so the server state can be confirmed.");
      return;
    }
    setDrafts({});
    setError("");
    setNotice("Pending stock changes discarded.");
  };

  const requireStockUnlock = (): boolean => {
    if (stockUnlocked) return true;
    setError("");
    setNotice("");
    openUnlock();
    return false;
  };

  const handleStockMutationError = (err: any, fallback: string) => {
    if (err?.code === "STOCK_UNLOCK_REQUIRED" || err?.code === "INVALID_STOCK_EDIT_TOKEN") {
      clearStockEditToken();
      setStockEditToken("");
      setUnlockOpen(true);
    }
    if (isUnconfirmedStockMutationError(err)) {
      setStockReconciliationRequired(true);
    }
    setError(err?.message ?? fallback);
  };

  const revalidateAfterSave = async (successMessage: string) => {
    setNotice(successMessage);
    try {
      await refresh(debouncedSearch, { allowWithDrafts: true, forceRefresh: true });
    } catch {
      setNotice(`${successMessage} Latest stock data could not be refreshed; use Refresh when the connection improves.`);
    }
  };

  const submitUnlock = async (event: FormEvent) => {
    event.preventDefault();
    setUnlockError("");
    setUnlockBusy(true);
    try {
      const result = await api.verifyStockPin(unlockPin);
      persistStockEditToken(result.token);
      setStockEditToken(result.token);
      setUnlockPin("");
      setUnlockOpen(false);
      setNotice("Stock editing unlocked for this tab.");
    } catch (err: any) {
      setUnlockError(err?.message ?? "Invalid Stock PIN");
    } finally {
      setUnlockBusy(false);
    }
  };

  const saveAllDrafts = async () => {
    setError("");
    setNotice("");
    if (!requireStockUnlock()) return;
    if (stockReconciliationRequired) {
      setError("Reconcile Stock before saving again so any change the server already received is confirmed.");
      return;
    }

    const name = editorName.trim();
    if (!name) {
      setError("Your Name is required to save stock changes.");
      return;
    }

    let rows: Array<{
      stockLotId: string;
      updatedAt: string | null;
      parUpdatedAt: string | null;
      quantity: number;
      parQty: number;
      expirationDate: string | null;
      binLabel: string | null;
      notes: string;
    }> = [];
    try {
      rows = buildPayloadRows();
    } catch (err: any) {
      setError(err?.message ?? "Invalid stock changes");
      return;
    }

    if (rows.length === 0) {
      setNotice("No pending stock changes.");
      return;
    }

    setSaving(true);
    const results: Array<Awaited<ReturnType<typeof api.bulkUpdateStock>>> = [];
    let confirmedCount = 0;
    try {
      const chunkSize = 8;
      for (let index = 0; index < rows.length; index += chunkSize) {
        const chunk = rows.slice(index, index + chunkSize);
        const chunkResult = await api.bulkUpdateStock(
          {
            enteredName: name,
            note: "Bulk stock table save",
            rows: chunk
          },
          stockEditToken
        );
        if (!isConfirmedStockSaveChunk(chunkResult, chunk.length)) {
          throw new Error("The server did not confirm every row in the latest save batch.");
        }

        results.push(chunkResult);
        confirmedCount += chunk.length;
        const confirmedStockLotIds = chunk.map((row) => row.stockLotId);
        setItems((prev) => applyConfirmedStockRows(prev, chunk));
        setDrafts((prev) => removeConfirmedStockDrafts(prev, confirmedStockLotIds));
      }
      const result = aggregateStockSaveResults(results);
      rememberName(name);
      const parLabel = result.updatedParCount ? `, ${result.updatedParCount} PAR ${result.updatedParCount === 1 ? "value" : "values"}` : "";
      await revalidateAfterSave(
        `Saved ${result.updatedStockCount ?? result.updatedCount} stock rows${parLabel} (${result.unchangedCount} unchanged).`
      );
    } catch (err: any) {
      handleStockMutationError(err, "Failed to save stock changes");
      if (confirmedCount > 0) rememberName(name);

      setStockReconciliationRequired(true);
      let refreshSucceeded = false;
      try {
        await forceReconcileStock();
        refreshSucceeded = true;
      } catch {
        refreshSucceeded = false;
      }

      setNotice("");
      setError(
        partialStockSaveMessage({
          confirmedCount,
          totalCount: rows.length,
          refreshSucceeded,
          failureMessage: err?.message ?? "The stock save could not be completed."
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const loadMoveOptions = async () => {
    if (moveOptions.length > 0) return moveOptions;
    const directory = await api.getApparatusDirectory();
    const options = directory.apparatus
      .map((entry) => ({
        apparatusId: entry.id,
        apparatusName: entry.name,
        bags: entry.bags
      }))
      .filter((entry) => entry.bags.length > 0);
    setMoveOptions(options);
    return options;
  };

  const openMoveFlow = async (lot: StockLotItem) => {
    setError("");
    setNotice("");
    if (!requireStockUnlock()) return;
    if (lot.isBackorder) {
      setNotice("Backorder lots cannot be moved to apparatus.");
      return;
    }
    try {
      const options = await loadMoveOptions();
      if (options.length === 0) {
        setNotice("No destination bags available.");
        return;
      }
      setMoveSelected(lot);
    } catch (err: any) {
      setError(err?.message ?? "Failed to load destination apparatus");
    }
  };

  const moveToBag = async (lot: StockLotItem, payload: { enteredName: string; quantity: number; bagId: string }) => {
    const name = payload.enteredName.trim();
    if (!name) {
      throw new Error("Your Name is required.");
    }
    rememberName(name);

    try {
      await api.moveStockToBag(
        {
          stockLotId: lot.stockLotId,
          toBagId: payload.bagId,
          quantity: Math.round(payload.quantity),
          note: null,
          enteredName: name
        },
        stockEditToken
      );

      await revalidateAfterSave("Stock moved successfully.");
    } catch (err: any) {
      handleStockMutationError(err, "Failed to move stock");
      throw err;
    }
  };

  const deleteStockLine = async (item: StockLotItem) => {
    setError("");
    setNotice("");
    if (!requireStockUnlock()) return;

    const name = editorName.trim();
    if (!name) {
      setError("Your Name is required to delete a stock line item.");
      return;
    }

    const draft = getDraft(item);
    setDeleteBusy(true);
    try {
      await api.updateStockLot(
        {
          stockLotId: item.stockLotId,
          quantity: 0,
          expirationDate: null,
          binLabel: draft.binLabel.trim() ? draft.binLabel.trim() : null,
          notes: draft.notes,
          enteredName: name,
          note: "Delete stock line item (set quantity to 0)"
        },
        stockEditToken
      );
      rememberName(name);
      setDrafts((prev) => {
        if (!prev[item.stockLotId]) return prev;
        const next = { ...prev };
        delete next[item.stockLotId];
        return next;
      });
      setDeleteConfirmItem(null);
      await revalidateAfterSave(`Deleted ${item.itemName} line item (quantity set to 0).`);
    } catch (err: any) {
      handleStockMutationError(err, "Failed to delete stock line item");
    } finally {
      setDeleteBusy(false);
    }
  };

  const exportCsv = async () => {
    setError("");
    setNotice("");
    try {
      const result = await api.getStockInventory("");
      const csv = `\uFEFF${stockRowsToCsv(result.items)}`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      a.href = url;
      a.download = `stock-export-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setNotice(`Exported ${result.items.length} stock rows.`);
    } catch (err: any) {
      setError(err?.message ?? "Failed to export stock CSV");
    }
  };

  const onPickImport = () => {
    if (!requireStockUnlock()) return;
    if (pendingDraftsRef.current) {
      setNotice("Save or discard pending stock edits before importing a different stock view.");
      return;
    }
    fileInputRef.current?.click();
  };

  const onImportFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!requireStockUnlock()) return;

    setError("");
    setNotice("");
    setImporting(true);
    cancelStockRead();

    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (parsed.length < 2) throw new Error("CSV must include a header row and at least one data row.");

      const headers = parsed[0].map((value) => normalizeHeader(value));
      const stockLotIdIndex = headers.findIndex((value) => value === "stocklotid");
      const binLabelIndex = headers.findIndex((value) => value === "binlabel");
      const quantityIndex = headers.findIndex((value) => value === "quantity");
      const parQtyIndex = headers.findIndex((value) => value === "parqty" || value === "par");
      const expirationIndex = headers.findIndex((value) => value === "expirationdate");
      const notesIndex = headers.findIndex((value) => value === "notes");

      if (stockLotIdIndex < 0) throw new Error("CSV must include a stockLotId column.");
      if (quantityIndex < 0 && parQtyIndex < 0 && expirationIndex < 0 && notesIndex < 0 && binLabelIndex < 0) {
        throw new Error("CSV must include at least one editable column: binLabel, quantity, parQty, expirationDate, or notes.");
      }

      const result = await api.getStockInventory("");
      const sourceById = new Map(result.items.map((item) => [item.stockLotId, item]));

      const nextDrafts: Record<string, StockDraft> = {};
      const missingIds: string[] = [];

      for (let i = 1; i < parsed.length; i += 1) {
        const row = parsed[i];
        const stockLotId = (row[stockLotIdIndex] ?? "").trim();
        if (!stockLotId) continue;

        const source = sourceById.get(stockLotId);
        if (!source) {
          missingIds.push(stockLotId);
          continue;
        }

        const base = draftFromStockItem(source);

        const binLabelRaw = binLabelIndex >= 0 ? row[binLabelIndex] ?? "" : "";
        const quantityRaw = quantityIndex >= 0 ? (row[quantityIndex] ?? "").trim() : "";
        const parQtyRaw = parQtyIndex >= 0 ? (row[parQtyIndex] ?? "").trim() : "";
        const expirationRaw = expirationIndex >= 0 ? (row[expirationIndex] ?? "").trim() : "";
        const normalizedExpiration = expirationRaw ? normalizeExpirationDate(expirationRaw) : null;
        if (expirationRaw && !normalizedExpiration) {
          throw new Error(`CSV row ${i + 1} has an invalid expiration month '${expirationRaw}'. Use MM/YYYY or YYYY-MM.`);
        }
        const notesRaw = notesIndex >= 0 ? row[notesIndex] ?? "" : "";

        nextDrafts[stockLotId] = {
          binLabel: binLabelIndex >= 0 ? binLabelRaw.trim() : base.binLabel,
          quantity: quantityIndex >= 0 && quantityRaw !== "" ? normalizeQuantityInput(quantityRaw) : base.quantity,
          parQty: parQtyIndex >= 0 && parQtyRaw !== "" ? normalizeQuantityInput(parQtyRaw) : base.parQty,
          expirationDate: expirationIndex >= 0 ? normalizedExpiration ?? "" : base.expirationDate,
          notes: notesIndex >= 0 ? notesRaw : base.notes
        };
      }

      setSearch("");
      setStatusFilter("all");
      setItems(result.items);
      setDrafts((prev) => ({ ...prev, ...nextDrafts }));

      if (missingIds.length > 0) {
        setNotice(`Imported ${Object.keys(nextDrafts).length} rows. ${missingIds.length} rows did not match stockLotId.`);
      } else {
        setNotice(`Imported ${Object.keys(nextDrafts).length} rows. Review and click Save Changes.`);
      }
    } catch (err: any) {
      setError(err?.message ?? "Failed to import CSV");
    } finally {
      setImporting(false);
    }
  };

  const openAddStock = () => {
    if (!requireStockUnlock()) return;
    setShowAdd(true);
    setAddModalMode("add");
    setAddNewItemMode(false);
    setAddUseCustomBin(false);
    setAddForm(initialAddForm());
    setAddResults([]);
    setAddBinSuggestions([]);
  };

  const openSplitStock = (item: StockLotItem) => {
    if (!requireStockUnlock()) return;
    const currentBin = (item.binLabel ?? "").trim();
    setShowAdd(true);
    setAddModalMode("split");
    setAddNewItemMode(false);
    setAddUseCustomBin(false);
    setAddBinSuggestions(currentBin ? [currentBin] : []);
    setAddForm({
      query: item.itemName,
      selectedItem: {
        id: item.catalogItemId,
        canonicalName: "",
        displayName: item.itemName,
        category: item.category,
        subcategory: item.subcategory,
        sizeNotes: null,
        isExpiring: true,
        allowedInStock: true
      },
      quantity: "",
      expirationDate: "",
      binLabel: currentBin,
      notes: item.notes ?? ""
    });
    setAddResults([]);
  };

  const closeAddStock = () => {
    setShowAdd(false);
    setAddModalMode("add");
    setAddNewItemMode(false);
    setAddUseCustomBin(false);
    setAddForm(initialAddForm());
    setAddResults([]);
    setAddBinSuggestions([]);
    setAddLoading(false);
  };

  const selectedBinOption = useMemo(() => {
    if (addUseCustomBin || addBinSuggestions.length === 0) return "__custom__";
    const current = addForm.binLabel.trim();
    if (current && addBinSuggestions.includes(current)) return current;
    return addBinSuggestions[0];
  }, [addForm.binLabel, addBinSuggestions, addUseCustomBin]);

  useEffect(() => {
    if (!showAdd || addModalMode !== "add") return;
    const shouldDefaultQuantity = addNewItemMode || !!addForm.selectedItem;
    setAddForm((prev) => {
      if (shouldDefaultQuantity) {
        if (prev.quantity !== "") return prev;
        return {
          ...prev,
          quantity: "1"
        };
      }
      if (prev.quantity === "") return prev;
      return {
        ...prev,
        quantity: ""
      };
    });
  }, [showAdd, addModalMode, addNewItemMode, addForm.selectedItem?.id]);

  const submitAddStock = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    if (!requireStockUnlock()) return;

    const name = editorName.trim();
    if (!name) {
      setError("Your Name is required to add stock.");
      return;
    }

    if (!addForm.selectedItem) {
      setError("Select a stock item.");
      return;
    }

    const quantity = parseQuantityInput(addForm.quantity);
    if (quantity <= 0) {
      setError("Quantity must be at least 1.");
      return;
    }

    setAddBusy(true);
    try {
      let allowListCreated = false;
      if (addModalMode === "add" && !addForm.selectedItem.allowedInStock) {
        const allowResult = await api.allowStockItem(
          {
            catalogItemId: addForm.selectedItem.id,
            enteredName: name,
            note: "Added to stock allow list from stock add modal"
          },
          stockEditToken
        );
        allowListCreated = allowResult.created;
      }

      await api.addStockLot(
        {
          catalogItemId: addForm.selectedItem.id,
          quantity,
          expirationDate: addForm.expirationDate ? addForm.expirationDate : null,
          binLabel: addForm.binLabel.trim() ? addForm.binLabel.trim() : null,
          notes: addForm.notes,
          enteredName: name,
          note: "Added from stock workspace"
        },
        stockEditToken
      );
      rememberName(name);
      closeAddStock();
      if (addModalMode === "split") {
        await revalidateAfterSave("Split item added.");
      } else {
        await revalidateAfterSave(allowListCreated ? "Item added to allow list and stock." : "Stock item added.");
      }
    } catch (err: any) {
      handleStockMutationError(err, "Failed to add stock item");
    } finally {
      setAddBusy(false);
    }
  };

  return (
    <div className="stack-md">
      {error ? <p className="error-text">{error}</p> : null}
      {notice ? <p className="muted small">{notice}</p> : null}

      <section className="card stack-sm">
        <div className="row-between">
          <h2>Stock Workspace</h2>
          <div className="stock-lock-actions">
            <span className={`stock-lock-pill ${stockUnlocked ? "stock-lock-pill-unlocked" : "stock-lock-pill-locked"}`}>
              {stockUnlocked ? "Unlocked" : "Locked"}
            </span>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={stockUnlocked ? lockStockEditing : openUnlock}
              disabled={stockReconciliationRequired}
            >
              {stockUnlocked ? "Lock" : "Unlock"}
            </button>
            <p className="muted small" role="status" aria-live="polite">
              {pendingCount} pending changes{stockLoading ? " · Updating…" : ""}
            </p>
          </div>
        </div>
        {!stockUnlocked ? (
          <p className="muted small">Stock is view-only until unlocked. Export and search still work.</p>
        ) : null}
        {stockReconciliationRequired ? (
          <div className="stack-xs" role="alert">
            <p className="warning-text small">
              A Stock change was not confirmed. Reconcile with the server before retrying, discarding, or locking.
            </p>
            <div>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={reconcileStockView}
                disabled={stockBusy}
              >
                {stockLoading ? "Reconciling…" : "Reconcile Stock"}
              </button>
            </div>
          </div>
        ) : null}

        <div className="stock-toolbar">
          <label className="stack-xs">
            <span className="label">Search</span>
            <input
              className="input"
              placeholder="Search stock"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              disabled={pendingCount > 0 || saving}
            />
            {pendingCount > 0 ? (
              <span className="muted small">Save or discard pending edits before changing filters.</span>
            ) : null}
          </label>

          <label className="stack-xs">
            <span className="label">Status</span>
            <select
              className="input"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as StockStatusFilter)}
              disabled={pendingCount > 0 || saving}
            >
              {STOCK_STATUS_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="stack-xs">
            <span className="label">Your Name</span>
            <input className="input" placeholder="Your Name" value={editorName} onChange={(event) => setEditorName(event.target.value)} />
          </label>
        </div>

        <div className="actions-wrap">
          <button className="btn btn-secondary" onClick={openAddStock} disabled={!stockUnlocked}>
            Add Stock Item
          </button>
          <button className="btn btn-secondary" onClick={exportCsv}>
            Export CSV
          </button>
          <button
            className="btn btn-secondary"
            onClick={onPickImport}
            disabled={importing || pendingCount > 0 || !stockUnlocked}
          >
            {importing ? "Importing..." : "Import CSV"}
          </button>
          <button
            className="btn btn-ghost"
            type="button"
            onClick={discardDrafts}
            disabled={saving || pendingCount === 0 || stockReconciliationRequired}
          >
            Discard Changes
          </button>
          <button
            className="btn btn-primary"
            onClick={saveAllDrafts}
            disabled={saving || pendingCount === 0 || !stockUnlocked || stockReconciliationRequired}
          >
            {saving ? "Saving..." : `Save Changes (${pendingCount})`}
          </button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={onImportFile} />
        </div>
      </section>

      <section className="card stack-sm">
        <div className="stock-table-wrap">
          <table className="stock-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Bin</th>
                <th>In Stock</th>
                <th>PAR</th>
                <th>Expiration</th>
                <th>Notes</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {stockLoading && items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="muted small">
                    Loading stock…
                  </td>
                </tr>
              ) : null}
              {!stockLoading && visibleItems.length === 0 ? (
                <tr>
                  <td colSpan={8} className="muted small">
                    {search.trim() && statusFilter !== "all"
                      ? "No stock matches the current search and selected status."
                      : statusFilter !== "all"
                        ? "No stock matches the selected status."
                        : search.trim()
                          ? "No stock matches the current search."
                          : "No stock rows are available."}
                  </td>
                </tr>
              ) : null}
              {visibleItems.map((item) => {
                const draft = getDraft(item);
                const dirty = hasDraft(item);
                const relatedMeta = relatedMetaByStockLotId[item.stockLotId];
                const rowClassName = [dirty ? "stock-row-dirty" : "", relatedRowClass(relatedMeta)].filter(Boolean).join(" ");

                return (
                  <tr key={item.stockLotId} className={rowClassName}>
                    <td className="stock-item-cell">
                      <div className="row gap-sm search-result-meta">
                        <strong className="text-wrap-anywhere">{item.itemName}</strong>
                      </div>
                      <p className="muted small">
                        {item.category ?? "Other"}
                        {item.subcategory ? ` • ${item.subcategory}` : ""}
                      </p>
                    </td>
                    <td className="stock-bin-cell">
                      <input
                        className="input stock-cell-input stock-bin-input"
                        value={draft.binLabel}
                        onChange={(event) => updateDraft(item.stockLotId, { binLabel: event.target.value })}
                        placeholder="eg: R-S8"
                        disabled={stockTableEditingDisabled}
                      />
                    </td>
                    <td>
                      <input
                        className="input stock-cell-input stock-qty-input"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={draft.quantity}
                        onFocus={(event) => event.currentTarget.select()}
                        onChange={(event) => {
                          const nextQuantity = normalizeQuantityInput(event.target.value);
                          updateDraft(item.stockLotId, {
                            quantity: nextQuantity,
                            expirationDate: nextQuantity === "0" ? "" : draft.expirationDate
                          });
                        }}
                        onBlur={(event) => {
                          const nextQuantity = normalizeQuantityInput(event.target.value);
                          if (nextQuantity === "") {
                            updateDraft(item.stockLotId, { quantity: "0", expirationDate: "" });
                          }
                        }}
                        disabled={stockTableEditingDisabled}
                      />
                    </td>
                    <td className="stock-par-cell">
                      <input
                        className="input stock-cell-input stock-par-input"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={draft.parQty}
                        onFocus={(event) => event.currentTarget.select()}
                        onChange={(event) => updateParDraft(item, event.target.value)}
                        onBlur={(event) => {
                          const nextPar = normalizeQuantityInput(event.target.value);
                          if (nextPar === "") {
                            updateParDraft(item, "0");
                          }
                        }}
                        aria-label={`PAR for ${item.itemName}`}
                        disabled={stockTableEditingDisabled}
                      />
                    </td>
                    <td className="stock-date-cell">
                      <ExpirationMonthInput
                        label={`Expiration for ${item.itemName}`}
                        value={draft.expirationDate}
                        onChange={(expirationDate) => updateDraft(item.stockLotId, { expirationDate })}
                        disabled={stockTableEditingDisabled}
                        allowEmpty
                        hideLabel
                      />
                    </td>
                    <td>
                      <input
                        className="input stock-cell-input"
                        value={draft.notes}
                        onChange={(event) => updateDraft(item.stockLotId, { notes: event.target.value })}
                        placeholder="eg: backorder"
                        disabled={stockTableEditingDisabled}
                      />
                    </td>
                    <td>
                      <StatusBadge severity={item.status.severity} text={item.status.reason ?? undefined} />
                    </td>
                    <td>
                      <div className="actions-wrap stock-row-actions">
                        <button
                          className="btn btn-secondary"
                          onClick={() => openMoveFlow(item)}
                          disabled={stockTableEditingDisabled || item.isBackorder || item.quantity <= 0}
                        >
                          Move
                        </button>
                        <button className="btn btn-ghost" onClick={() => openSplitStock(item)} disabled={stockTableEditingDisabled}>
                          Split
                        </button>
                        <button className="btn btn-danger" onClick={() => setDeleteConfirmItem(item)} disabled={stockTableEditingDisabled}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {showAdd ? (
        <div className="modal-backdrop" onClick={() => (addBusy ? undefined : closeAddStock())}>
          <div className="modal-card move-sheet-card" onClick={(event) => event.stopPropagation()}>
            <ModalCloseButton onClick={closeAddStock} disabled={addBusy} />
            <h3>{addModalMode === "split" ? "Split Stock Item" : "Add Stock Item"}</h3>
            <form className="stack-sm" onSubmit={submitAddStock}>
              {addModalMode === "add" ? (
                <>
                  <label className="stack-xs">
                    <span className="label">Item Search</span>
                    <div className="small muted" style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.35rem" }}>
                      <input
                        type="checkbox"
                        checked={addNewItemMode}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setAddNewItemMode(checked);
                          setAddUseCustomBin(checked);
                          setAddResults([]);
                          setAddBinSuggestions([]);
                          setAddForm((prev) => ({
                            ...prev,
                            selectedItem: null,
                            binLabel: ""
                          }));
                        }}
                      />
                      <span>Add new item to stock</span>
                    </div>
                    <input
                      className="input"
                      value={addForm.query}
                      onChange={(event) =>
                        setAddForm((prev) => ({
                          ...prev,
                          query: event.target.value,
                          selectedItem: null,
                          binLabel: ""
                        }))
                      }
                      placeholder={addNewItemMode ? "Enter item name to add to stock" : "Search allowed stock items"}
                    />
                  </label>

                  {addLoading ? <p className="muted small">Searching...</p> : null}

                  {addForm.selectedItem ? (
                    <p className="small">
                      <strong>Selected:</strong> {addForm.selectedItem.displayName}
                    </p>
                  ) : null}
                  {addForm.selectedItem && !addForm.selectedItem.allowedInStock ? (
                    <p className="muted small">This item is not on the allow list yet and will be added.</p>
                  ) : null}

                  {addResults.length > 0 ? (
                    <div className="list stock-add-results">
                      {addResults.map((result) => (
                        <button
                          key={result.id}
                          type="button"
                          className="list-item btn-link"
                          onClick={() => {
                            setAddUseCustomBin(false);
                            setAddForm((prev) => ({
                              ...prev,
                              query: result.displayName,
                              selectedItem: result,
                              binLabel: ""
                            }));
                          }}
                        >
                          <strong className="text-wrap-anywhere">{result.displayName}</strong>
                          {!result.allowedInStock ? <span className="muted small">Will be added to allow list</span> : null}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : addForm.selectedItem ? (
                <p className="small">
                  <strong>Item:</strong> {addForm.selectedItem.displayName}
                </p>
              ) : null}

              <label className="stack-xs">
                <span className="label">Quantity</span>
                <input
                  className="input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={addForm.quantity}
                  onFocus={(event) => event.currentTarget.select()}
                  onChange={(event) =>
                    setAddForm((prev) => ({
                      ...prev,
                      quantity: normalizeQuantityInput(event.target.value)
                    }))
                  }
                  onBlur={() => {
                    if (!addForm.quantity && (addModalMode !== "add" || addNewItemMode || !!addForm.selectedItem)) {
                      setAddForm((prev) => ({ ...prev, quantity: "1" }));
                    }
                  }}
                />
              </label>

              <ExpirationMonthInput
                label="Expiration"
                value={addForm.expirationDate}
                onChange={(expirationDate) => setAddForm((prev) => ({ ...prev, expirationDate }))}
                allowEmpty
              />

              <label className="stack-xs">
                <span className="label">Bin</span>
                <select
                  className="input"
                  value={selectedBinOption}
                  onChange={(event) => {
                    const selected = event.target.value;
                    if (selected === "__custom__") {
                      setAddUseCustomBin(true);
                      setAddForm((prev) => ({
                        ...prev,
                        binLabel: ""
                      }));
                      return;
                    }
                    setAddUseCustomBin(false);
                    setAddForm((prev) => ({
                      ...prev,
                      binLabel: selected
                    }));
                  }}
                >
                  {addBinSuggestions.map((bin) => (
                    <option key={bin} value={bin}>
                      {bin}
                    </option>
                  ))}
                  <option value="__custom__">Enter new bin</option>
                </select>
              </label>

              <label className="stack-xs">
                <span className="label">New bin:</span>
                <input
                  className="input"
                  value={addForm.binLabel}
                  onChange={(event) => setAddForm((prev) => ({ ...prev, binLabel: event.target.value }))}
                  placeholder="eg: R-S8"
                  disabled={!addUseCustomBin}
                />
              </label>

              <label className="stack-xs">
                <span className="label">Notes</span>
                <input
                  className="input"
                  value={addForm.notes}
                  onChange={(event) => setAddForm((prev) => ({ ...prev, notes: event.target.value }))}
                  placeholder="eg: backorder"
                />
              </label>

              <div className="actions-wrap">
                <button type="button" className="btn btn-secondary" onClick={closeAddStock} disabled={addBusy}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={addBusy || !addForm.selectedItem || parseQuantityInput(addForm.quantity) <= 0}
                >
                  {addBusy ? "Adding..." : addModalMode === "split" ? "Add Split Item" : "Add Item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deleteConfirmItem ? (
        <div className="modal-backdrop" onClick={() => (deleteBusy ? undefined : setDeleteConfirmItem(null))}>
          <div className="modal-card move-sheet-card" onClick={(event) => event.stopPropagation()}>
            <ModalCloseButton onClick={() => setDeleteConfirmItem(null)} disabled={deleteBusy} />
            <h3>Delete Stock Line Item</h3>
            <p className="muted small">
              This sets quantity to 0 and clears expiration for this line item while keeping the item structure intact.
            </p>
            <div className="card-soft stack-xs">
              <p>
                <strong>{deleteConfirmItem.itemName}</strong>
              </p>
              <p className="muted small">
                Qty {getDraft(deleteConfirmItem).quantity || "0"}
                {getDraft(deleteConfirmItem).binLabel ? ` • Bin ${getDraft(deleteConfirmItem).binLabel}` : ""}
                {getDraft(deleteConfirmItem).expirationDate ? ` • Exp ${formatExpirationMonth(getDraft(deleteConfirmItem).expirationDate)}` : ""}
              </p>
            </div>
            <div className="actions-wrap" style={{ marginTop: "0.75rem" }}>
              <button type="button" className="btn btn-secondary" onClick={() => setDeleteConfirmItem(null)} disabled={deleteBusy}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  if (!deleteConfirmItem) return;
                  void deleteStockLine(deleteConfirmItem);
                }}
                disabled={deleteBusy}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {moveSelected ? (
        <MoveStockSheet
          open
          title={`Move ${moveSelected.itemName}`}
          maxQuantity={Math.max(1, moveSelected.quantity)}
          initialName={editorName}
          apparatusOptions={moveOptions}
          onClose={() => setMoveSelected(null)}
          onMove={(value) =>
            moveToBag(moveSelected, {
              enteredName: value.enteredName,
              quantity: value.quantity,
              bagId: value.bagId
            })
          }
        />
      ) : null}

      {unlockOpen ? (
        <div className="modal-backdrop" onClick={() => (unlockBusy ? undefined : setUnlockOpen(false))}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <ModalCloseButton onClick={() => setUnlockOpen(false)} disabled={unlockBusy} />
            <h3>Unlock Stock Editing</h3>
            <p className="muted small">Stock editing is unlocked for this browser tab only.</p>
            <form className="stack-sm" onSubmit={submitUnlock}>
              <label className="stack-xs">
                <span className="label">PIN</span>
                <input
                  autoFocus
                  className="input"
                  type="password"
                  inputMode="numeric"
                  value={unlockPin}
                  onChange={(event) => setUnlockPin(event.target.value)}
                  placeholder="Enter Stock PIN"
                  maxLength={32}
                />
              </label>
              {unlockError ? <p className="error-text">{unlockError}</p> : null}
              <div className="row-end gap-sm">
                <button type="button" className="btn btn-secondary" onClick={() => setUnlockOpen(false)} disabled={unlockBusy}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={unlockBusy || unlockPin.trim().length === 0}>
                  {unlockBusy ? "Unlocking..." : "Unlock"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
